import { AlgoliaProvider } from "@components/algolia/AlgoliaProvider";
import { renderRichTextToComponent } from "@components/cms/rich-text-renderer/renderRichTextToComponent";
import { Banner } from "@components/commerce/categories/banner";
import { ProductListing } from "@components/commerce/product-listing/ProductListing";
import { FiltersController } from "@components/commerce/product-listing/filters/context/FiltersContextProvider";
import { PageSeo } from "@components/seo/PageSeo";
import { CategoryNavigation } from "@components/ui/category-navigation/CategoryNavigation";
import { getSiteSettings } from "@config/site/site-api-settings";
import { getPageContentBySlug } from "@contentful-api/queries/pagePath/pagePathService";
import { getSiteSettingsContent } from "@contentful-api/queries/siteSettings/siteSettingsService";
import type {
    ILink,
    IPageProductCategoryPage,
    IPageProductListingPage,
    ISiteSettings,
} from "@contentful-api/types/contentful";
import type { Document } from "@contentful/rich-text-types";
import { CommonCMS } from "@lib/constants/contentful";
import type { PagePath } from "@lib/constants/pagePaths";
import { getPagePathByLocale } from "@lib/constants/pagePaths";
import { MicrocopyProvider, useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { GetLiveUpdates, ModulesRenderer } from "@lib/contentful/modules-renderer/ModulesRenderer";
import { PageTypes } from "@lib/enums/PageTypes";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { updatePageHotspotModules } from "@lib/utils/hotspotsHelper";
import { getAdditionalSizesFilters } from "@lib/utils/plp/plpShoeSizes";
import { getInitialFiltersList } from "@lib/utils/productListingPageUtils";
import { ZustandProvider } from "@store/index";
import { PageHeading } from "@ui/components/content/page-heading/PageHeading";
import { Box } from "@ui/components/layout/box/Box";
import { deserialize, serialize } from "@ungap/structured-clone";
import type { GetServerSidePropsContext, PreviewData } from "next";
import { useRouter } from "next/router";
import type { ParsedUrlQuery } from "querystring";
import type { FC } from "react";
import { renderToString } from "react-dom/server";
import { getServerState } from "react-instantsearch";

export type ProductCategoryPageProps = Awaited<
    ReturnType<typeof generateGetServerSidePropsForCategoryPage>
>["props"];

export const ProductCategoryPage: FC<ProductCategoryPageProps> = ({
    serializedSiteSettings,
    serializedPage,
    pathName,
}) => {
    const page: IPageProductCategoryPage = deserialize(serializedPage);
    const siteSettings: ISiteSettings = deserialize(serializedSiteSettings);

    const { isPreview } = useRouter();
    const updatedPage = isPreview ? (GetLiveUpdates(page) as IPageProductCategoryPage) : page;

    const { fields: { authorizationInvitation } = {} } = siteSettings;
    const {
        fields: {
            title,
            desc,
            titleSize = "Extra Large",
            descriptionSize = "Normal",
            modules,
            subNavigationItems,
            seoMetadata,
        },
        sys: { id } = {},
    } = updatedPage;
    const itemsWithLinkImages = (subNavigationItems?.fields?.links || []).filter(
        (item) => (item as ILink)?.fields?.linkImage?.fields?.file?.url
    );

    const { getMultiple } = useMicrocopy();

    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.generic.readMore,
        CommonCMS.generic.readLess,
    ]);

    return (
        <>
            {seoMetadata && <PageSeo seoModule={seoMetadata} pathName={pathName} />}
            <Box mt={[5, 8]} data-contentful-entry-id={id} data-contentful-field-id={"title"}>
                <PageHeading
                    title={title}
                    description={renderRichTextToComponent(desc)}
                    showMoreLabel={microcopies[CommonCMS.generic.readMore]}
                    showLessLabel={microcopies[CommonCMS.generic.readLess]}
                    titleSize={titleSize}
                    descriptionSize={descriptionSize}
                />
            </Box>
            <main>
                {itemsWithLinkImages?.length === 0 ? (
                    <CategoryNavigation items={subNavigationItems} />
                ) : (
                    <Box ml={[3, 3, 7, 6]}>
                        <Banner bannerItems={subNavigationItems?.fields?.links} />
                    </Box>
                )}
                <ModulesRenderer
                    modules={modules}
                    authorizationInvitation={authorizationInvitation}
                />
            </main>
        </>
    );
};

export const generateGetServerSidePropsForCategoryPage = async (
    { locale, preview }: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>,
    path: PagePath
) => {
    const startTime = performance.now();

    const [siteSettings, pagePath] = await Promise.all([
        getSiteSettingsContent({
            locale,
            localeTagId: getSiteSettings.contentfulLocaleTagId,
            pageType: PageTypes.PCP,
            selectedFields: ["fields.sizeGuideModal"],
            microcopySetKey: "plp",
            isPreview: preview,
        }),
        getPageContentBySlug({
            locale,
            localeTagId: getSiteSettings.contentfulLocaleTagId,
            pageType: PageTypes.PCP,
            basePath: path,
            isPreview: preview,
        }),
    ]);

    if (!pagePath) {
        return {
            notFound: true,
        };
    }

    const { content, sharedMicrocopySet, pageMicrocopySet } = siteSettings ?? {};
    const pageTypeId = pagePath.fields?.page?.sys?.contentType?.sys?.id;
    const pageType = pageTypeId === "pageProductCategoryPage" ? PageTypes.PCP : PageTypes.PLP;

    // Serialize Contentful data to remove circular references and ensure it's JSON-safe for Next.js
    const serializedSiteSettings = serialize(content);
    const serializedSharedMicrocopySet = serialize(sharedMicrocopySet);
    const serializedPageMicrocopySet = serialize(pageMicrocopySet);
    const pathName = getPagePathByLocale(path, locale);

    if (pageType === PageTypes.PLP) {
        const pageData = pagePath.fields?.page as IPageProductListingPage;
        const serializedPage = serialize(pageData);

        const algoliaStartTime = performance.now();
        const filtersList = [
            ...(pageData?.fields?.filtersList ?? []),
            ...getAdditionalSizesFilters(pageData?.fields?.filtersList),
        ];
        const SSRFiltersList = filtersList.reduce((acc, it) => {
            acc[it.fields?.facetName] = [];
            return acc;
        }, {});

        const instantSearchServerState = await getServerState(
            <ZustandProvider initialState={{ products: { initialFilters: SSRFiltersList } }}>
                <MicrocopyProvider
                    value={{
                        shared: deserialize(serializedSharedMicrocopySet),
                        page: deserialize(serializedPageMicrocopySet),
                    }}
                >
                    <AlgoliaProvider locale={locale}>
                        <FiltersController
                            listOfFilters={filtersList}
                            sortByOptionsList={pageData?.fields?.sortByOptionsList}
                            pageCategory={pathName.slice(1).replaceAll("/", ">")}
                        >
                            <ProductListing
                                listOfFilters={filtersList}
                                authorizationInvitation={{} as Document}
                                locale={locale}
                            />
                        </FiltersController>
                    </AlgoliaProvider>
                </MicrocopyProvider>
            </ZustandProvider>,
            {
                renderToString,
            }
        );
        const initialFiltersList = getInitialFiltersList(
            instantSearchServerState,
            filtersList.map((it) => it.fields?.facetName)
        );
        const mappedFilters = filtersList.reduce((acc, filter) => {
            acc[filter.fields.filterName] = initialFiltersList[filter.fields.facetName]?.map(
                (it) => ({
                    value: it,
                    label: it,
                })
            );
            return acc;
        }, {});
        Logger.info(ServiceType.ALGOLIA, `${pageType} Algolia Query`, {
            tag: LogTag.PERFORMANCE,
            duration: performance.now() - algoliaStartTime,
            pageType: PageTypes.PLP,
        });

        Logger.info(ServiceType.NEXT_API, `${pageType} Total Time`, {
            tag: LogTag.PERFORMANCE,
            duration: performance.now() - startTime,
            pageType: PageTypes.PLP,
        });

        return {
            props: {
                serializedSiteSettings,
                serializedSharedMicrocopySet,
                serializedPageMicrocopySet,
                serializedPage,
                pathName,
                pageType: PageTypes.PLP,
                initialZustandState: {
                    products: {
                        list: [],
                        currentFilters: [],
                        resultsCount: null,
                        lastVisitedPlp: null,
                        algoliaQueryID: null,
                        initialFilters: mappedFilters,
                    },
                },
                algoliaFallbackState: instantSearchServerState,
            },
        };
    } else {
        const updatedPage = await updatePageHotspotModules(
            pagePath.fields.page as IPageProductCategoryPage,
            locale,
            PageTypes.PCP
        );

        const serializedPage = serialize(updatedPage);

        return {
            props: {
                serializedSiteSettings,
                serializedSharedMicrocopySet,
                serializedPageMicrocopySet,
                serializedPage,
                pathName,
                pageType: PageTypes.PCP,
            },
        };
    }
};
